import { action, computed, observable } from 'mobx';
import { setter, toggle } from 'mobx-decorators';
import get from 'lodash/get';
import moment from 'moment-timezone';
import AccountStore from 'stores/Model/Account/AccountStore';
import constants from 'Constants';
import * as configUtils from 'utils/Config';
import * as moneyUtils from 'utils/Money';
import { deserializeWithMappers, deserializeDate, setFieldValues } from 'stores/Utils';


class SubscriptionStore {

  @setter @observable id;
  @setter @observable account;
  @setter @observable package;
  @setter @observable product;
  @setter @observable plan;
  @setter @observable downgradedPlan;
  @setter @observable cancelled;

  @setter @observable price;
  @setter @observable isActive;
  @setter @observable isChargable;

  @setter @observable freeUntilDate;
  @setter @observable isExpiredFreeUntilDate;
  @setter @observable customPrice;


  static create(data={}) {
    const instance = new SubscriptionStore();
    setFieldValues(instance, data);
    return instance;
  }

  static getDefaults() {
    return {
      "id": null,
      "account": new AccountStore(),
      "package": "",
      "product": "",
      "plan": "",
      "downgradedPlan": "",
      "cancelled": false,

      "price": "",
      "isActive": false,
      "isChargable": false,

      "freeUntilDate": "",
      "isExpiredFreeUntilDate": "",
      "customPrice": "",
    };
  }

  constructor(defaults=SubscriptionStore.getDefaults()) {
    setFieldValues(this, defaults);
  }

  @action reset(defaults=SubscriptionStore.getDefaults()) {
    setFieldValues(this, defaults);
  }

  getDeserializeMappers() {
    return [
      {"field": "id"},
      {"field": "account", "type": constants.DESERIALIZE_TYPE_OBJECT, "cls": AccountStore},
      {"field": "package"},
      {"field": "product"},
      {"field": "plan"},
      {"field": "downgradedPlan", "accessor": "downgraded_plan"},
      {"field": "cancelled"},

      {"field": "price"},
      {"field": "isActive", "accessor": "is_active"},
      {"field": "isChargable", "accessor": "is_chargable"},

      {"field": "freeUntilDate", "accessor": "free_until_date", "type": constants.DESERIALIZE_TYPE_CUSTOM, "extract": deserializeDate},
      {"field": "isExpiredFreeUntilDate", "accessor": "is_expired_free_until_date"},
      {"field": "customPrice", "accessor": "custom_price"},
    ];
  }

  @action deserialize(data) {
    deserializeWithMappers(this, this.getDeserializeMappers(), data);
  }

  @computed get isSilverPackage() {
    return this.package === constants.SUBSCRIPTION_PACKAGE_SILVER;
  }

  @computed get isRubyPackage() {
    return this.package === constants.SUBSCRIPTION_PACKAGE_RUBY;
  }

  @computed get isGoldPackage() {
    return this.package === constants.SUBSCRIPTION_PACKAGE_GOLD;
  }

  @computed get isJadePackage() {
    return this.package === constants.SUBSCRIPTION_PACKAGE_JADE;
  }

  @computed get isDiamondPackage() {
    return this.package === constants.SUBSCRIPTION_PACKAGE_DIAMOND;
  }

  @computed get isCurrentPackage() {
    return this.package === constants.CURRENT_SUBSCRIPTION_PACKAGE;
  }

  @computed get packageDisplay() {
    const option = constants.SUBSCRIPTION_PACKAGE_OPTIONS.filter((option) => option.value === this.package)[0];
    if (option) return option.label;
    return "";
  }

  @computed get isAllInOnePlans() {
    return constants.ALL_IN_ONE_PLANS.includes(this.plan);
  }

  @computed get planOption() {
    return constants.PLAN_OPTIONS.filter((row) => row.value === this.plan)[0];
  }

  @computed get planTier() {
    const planOption = this.planOption;
    if (!planOption) return '';
    return planOption.tier || '';
  }

  @computed get downgradedPlanOption() {
    return constants.PLAN_OPTIONS.filter((row) => row.value === this.downgradedPlan)[0];
  }

  @computed get planName() {
    const option = this.planOption;
    if (option) return option.label;
    return "";
  }

  @computed get planNameWithPrice() {
    if (!this.planName) return "";
    if (this.freeUntilDate) {
      if (this.isExpiredFreeUntilDate) return `${this.planName} (Expired on ${this.freeUntilDateDisplay})`;
      return `${this.planName} (Free Until ${this.freeUntilDateDisplay})`;
    }

    if (this.customPrice) return `${this.planName} (Custom ${moneyUtils.formatAmountDisplay(this.customPrice)}/mo)`;
    return `${this.planName} (${moneyUtils.formatAmountDisplay(this.price)}/mo)`;
  }

  @computed get planNameWithPackage() {
    if (!this.planName) return "";
    return `${this.packageDisplay} - ${this.planNameWithPrice}`;
  }

  @computed get planShortname() {
    const option = this.planOption;
    if (option) return option.shortname;
    return "";
  }

  @computed get downgradedPlanShortname() {
    const option = this.downgradedPlanOption;
    if (option) return option.shortname;
    return "";
  }

  @computed get isGoldPlanV1() {
    return this.plan === constants.GOLD_PLAN_ALL_IN_ONE;
  }

  @computed get isGoldPlanV2() {
    let values = [];
    constants.GOLD_PLAN_V2_OPTIONS.forEach((option) => values.push(option.value));
    return values.includes(this.plan);
  }

  @computed get canUpdateFreeSubscription() {
    if (this.freeUntilDate) return true;
    return false;
  }

  @computed get canDelete() {
    if (!configUtils.isProduction()) return true;
    if (this.freeUntilDate) return true;
    return false;
  }

  @computed get freeUntilDateDisplay() {
    return this.freeUntilDate ? moment(this.freeUntilDate).format("D MMM YYYY") : "";
  }
}


export default SubscriptionStore;

